import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

export function LegalShell({
  title,
  intro,
  updated,
  children,
}: {
  title: string;
  intro: string;
  updated?: string;
  children: ReactNode;
}) {
  return (
    <section className="relative pt-32 pb-24">
      <div className="container-x max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-teal-deep hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to FitBuddy
        </Link>
        <h1 className="display mt-6 text-4xl text-navy sm:text-5xl">{title}</h1>
        <p className="mt-4 text-charcoal/75">{intro}</p>
        {updated && (
          <p className="mt-2 font-mono text-xs uppercase tracking-wide text-charcoal/60">
            Last updated {updated}
          </p>
        )}
        <div className="legal mt-10 space-y-8 text-charcoal/80">{children}</div>
      </div>
    </section>
  );
}

export function Block({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-navy">{heading}</h2>
      <div className="mt-3 space-y-3 leading-relaxed">{children}</div>
    </div>
  );
}

/** Plain bullet list styled for the legal pages. */
export function Points({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2 pl-1">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const linkCls = "font-medium text-teal-deep underline-offset-2 hover:underline";

export function Privacy() {
  usePageMeta({
    title: "Privacy Policy | FitBuddy",
    description:
      "How the FitBuddy app collects, stores, shares and deletes your data: weigh-ins, food logs, photos and AI coach chats. Plain English, no small print.",
    path: "/privacy",
  });
  return (
    <LegalShell
      title="Privacy Policy"
      intro="How FitBuddy collects, stores, shares and deletes your data, including the weight readings that power your calorie bank. Written in plain English."
      updated="9 October 2026"
    >
      <Block heading="Who we are">
        <p>
          FitBuddy (“we”, “us”) is the developer of the FitBuddy app on the App
          Store and Google Play, and of this website. We are the data controller
          for the personal data described here, and we process it under UK GDPR,
          the EU GDPR and the Data Protection Act 2018.
        </p>
      </Block>

      <Block heading="The data we collect">
        <p>
          We only collect what the app needs to work. Most of it is data you
          enter yourself.
        </p>
        <Points
          items={[
            <>
              <strong>Account data:</strong> your mobile number (you sign in
              with a one-time code sent by SMS) or, for some accounts, an email
              address and password. Passwords are stored as a secure hash, never
              in plain text.
            </>,
            <>
              <strong>Profile:</strong> your name, sex, age, height, goal
              weight, unit preferences, app settings and, if you set one, a
              profile picture.
            </>,
            <>
              <strong>Health and fitness data:</strong> weigh-ins, body
              measurements and body-fat estimates, food and drink logs, macros,
              workouts and exercise logs. Weight and body data is health data (a
              special category under GDPR Article 9), so we only process it with
              your explicit consent, which we ask for before your first weigh-in.
            </>,
            <>
              <strong>Photos:</strong> progress photos you choose to attach to a
              weigh-in are stored privately so only you can see them. Photos of
              a scale, a meal, a barcode or a menu are read on the spot to fill
              in a number or a food and are not kept.
            </>,
            <>
              <strong>AI coach chats:</strong> the messages and any attachments
              you send to the in-app coach, so the conversation stays in your
              history.
            </>,
            <>
              <strong>Connected services:</strong> if you link WHOOP, we store
              the connection and your daily activity and energy figures so they
              can feed your calorie bank. You can disconnect at any time.
            </>,
            <>
              <strong>Community:</strong> friends, friend requests, group
              memberships, posts, reactions and your shareable card, only if you
              use those features.
            </>,
            <>
              <strong>Payments:</strong> the app is free. If you order an
              optional physical product in the app (from the shop or a device
              pre-order), Stripe processes the payment. We store your order
              details, never your card details.
            </>,
            <>
              <strong>Diagnostics:</strong> error reports and, on this website,
              analytics that help us keep the product working (see the{" "}
              <Link to="/cookies" className={linkCls}>
                Cookie Policy
              </Link>
              ).
            </>,
          ]}
        />
      </Block>

      <Block heading="Why we use it">
        <p>
          To run your account and the earn-and-spend mechanic (working out your
          calorie bank from your weigh-ins and logs), to show your history,
          streaks and charts, to power the AI features you choose to use, to
          take payment for any shop orders you place, to answer support requests and to fix
          bugs. We do not sell your data, we do not show ads, and we never use
          your health data for advertising or profiling.
        </p>
      </Block>

      <Block heading="How it's stored">
        <p>
          Your data lives in a secure, European-hosted database (Supabase). Every
          table is protected by row-level security, so your account can only
          read its own rows. Data is encrypted in transit (HTTPS) and at rest.
          If you log while offline, entries are held on your phone until they
          sync and are cleared from the device when you sign out.
        </p>
      </Block>

      <Block heading="Who we share it with">
        <p>
          We share data only with the service providers that run parts of the
          app for us, under contracts that stop them using it for anything else:
        </p>
        <Points
          items={[
            "Supabase: database, sign-in and file storage.",
            "Twilio: delivers your SMS sign-in codes (your phone number only).",
            "Anthropic: powers the AI features (food and menu recognition, scale reading, meal ideas, the coach). Only the content needed for that request is sent, and it isn't used to train models.",
            "Stripe: takes payment for optional physical products ordered in the app (the shop and device pre-orders).",
            "WHOOP: only if you connect it, to read your activity data.",
            "Open Food Facts and similar food databases: receive barcode and food search terms only, never anything that identifies you.",
            "Sentry and Google Analytics: error monitoring and anonymised website analytics.",
          ]}
        />
        <p>
          Other FitBuddy users only see what you choose to share in the
          community features (for example “calories earned”, never your weight
          unless you post it). We disclose data to authorities only when the law
          requires it.
        </p>
      </Block>

      <Block heading="How long we keep it">
        <p>
          We keep your data for as long as your account exists. When you delete
          your account, everything tied to it is erased immediately; copies in
          our hosting provider's encrypted backups roll off within 30 days.
          Payment records held by Stripe are kept for as long as tax and
          accounting law requires. The full list of what goes and what stays is
          on the{" "}
          <Link to="/delete-account" className={linkCls}>
            Delete your account
          </Link>{" "}
          page.
        </p>
      </Block>

      <Block heading="Deleting your account and data">
        <p>
          You can delete your account and all of its data at any time, either in
          the app (Settings, then Delete account &amp; data) or on the web from
          our{" "}
          <Link to="/delete-account" className={linkCls}>
            account deletion page
          </Link>
          . Deletion is immediate and does not need anyone to approve it. If you
          only want a fresh start, Settings also lets you reset your data and
          keep your account.
        </p>
      </Block>

      <Block heading="Your rights">
        <p>
          You can access, correct, export or delete your data, object to or
          restrict processing, and withdraw consent at any time. Most of this is
          self-serve in the app: edit anything you've logged, export your
          history, or delete your account. For anything else, use the{" "}
          <Link to="/support" className={linkCls}>
            support page
          </Link>
          . You also have the right to complain to the UK Information
          Commissioner’s Office (ICO) or your local data protection authority.
        </p>
      </Block>

      <Block heading="Children">
        <p>
          FitBuddy is not meant for anyone under 18, and we don't knowingly
          collect data from children. If you believe a child has created an
          account, use the{" "}
          <Link to="/support" className={linkCls}>
            support page
          </Link>{" "}
          and we'll remove it.
        </p>
      </Block>

      <Block heading="Changes to this policy">
        <p>
          If we change how we use your data, we'll update this page and the date
          at the top, and tell you in the app before any significant change
          takes effect.
        </p>
      </Block>
    </LegalShell>
  );
}

export function Terms() {
  usePageMeta({
    title: "Terms of Service | FitBuddy",
    description:
      "The terms for using the FitBuddy app and website: your account, free use of the app, shop orders, acceptable use and the legal bits, in plain English.",
    path: "/terms",
  });
  return (
    <LegalShell
      title="Terms of Service"
      intro="The terms that govern your use of the FitBuddy app and website."
    >
      <Block heading="Using FitBuddy">
        <p>
          FitBuddy is a weight-loss companion that rewards logged progress with
          spendable calories. It is not a medical device and does not provide
          medical advice. Always follow the guidance of your own clinician,
          especially if you are using GLP-1 or other medication.
        </p>
      </Block>
      <Block heading="Accounts">
        <p>
          You’re responsible for keeping your login details secure and for
          activity on your account. You must be old enough to consent to data
          processing in your country to use FitBuddy.
        </p>
      </Block>
      <Block heading="Price">
        <p>
          The FitBuddy app is free to use, with every feature included. There is
          no subscription and no card needed. If you choose to buy an optional
          physical product in the app, you pay by card through Stripe and that
          purchase is covered by the order&apos;s own terms. Your payment details
          are held by Stripe, never on FitBuddy&apos;s servers.
        </p>
      </Block>
      <Block heading="Acceptable use">
        <p>
          Don’t misuse the service, attempt to disrupt it, or use it unlawfully.
          We may suspend accounts that do.
        </p>
      </Block>
      <Block heading="Liability & governing law">
        <p>
          FitBuddy is provided “as is”. To the extent permitted by law, we are
          not liable for indirect or consequential loss. These terms are governed
          by the laws of England and Wales. Questions? Visit the{" "}
          <Link to="/support" className={linkCls}>
            support page
          </Link>
          .
        </p>
      </Block>
    </LegalShell>
  );
}

export function Cookies() {
  usePageMeta({
    title: "Cookie Policy | FitBuddy",
    description:
      "What the FitBuddy website stores in your browser and why: strictly necessary items, consent-based analytics and error monitoring, and no advertising cookies.",
    path: "/cookies",
  });
  return (
    <LegalShell
      title="Cookie Policy"
      intro="What the FitBuddy website stores in your browser, and why."
    >
      <Block heading="What we use">
        <p>
          The marketing website uses a small number of cookies and similar
          technologies. Strictly-necessary items keep the site working. With your
          consent, we use privacy-friendly analytics (Google Analytics 4, with IP
          anonymisation) to understand which pages help people, and error
          monitoring to catch bugs.
        </p>
      </Block>
      <Block heading="Your choice">
        <p>
          Under UK PECR you control non-essential cookies. You can manage or
          withdraw consent at any time through your browser settings, and the
          site remains fully usable without analytics.
        </p>
      </Block>
      <Block heading="No advertising cookies">
        <p>
          We don’t use your browsing to build advertising profiles, and we don’t
          sell any data collected on this site.
        </p>
      </Block>
    </LegalShell>
  );
}
